import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import {
  getSite,
  createSignIn,
  setSignInSignaturePath,
  updateSite,
  listSignInPrefillSources,
  findSubcontractorByCompany,
  listActiveSignIns,
  bulkSignOut,
  type Site,
  type SubcontractorRow,
  type SignInPrefillSource,
  type SignIn,
} from '../lib/db';

import { uploadSignature } from '../lib/storage';
import { captureGeolocation, distanceMeters } from '../lib/geo';
import { LoadingIndicator } from '../components/LoadingIndicator';

// Worker confirmations — the four ticks every site labourer / subbie is
// expected to acknowledge before they go on site.
const WORKER_CONFIRMATIONS = [
  (orgName: string) => `I've been inducted on this site by ${orgName}`,
  () => "I'll wear hard hat, boots and hi-vis at all times",
  () => "I've read my RAMS for today's work and understand it",
  () => "If I'm not sure something is safe — I'll stop and ask",
];

// Visitor confirmations — shorter, because the visitor isn't doing site work.
// Delivery drivers, surveyors, inspectors, family etc. The legal hook is
// "they wear PPE provided + they stay with their host".
const VISITOR_CONFIRMATIONS = [
  () => "I'll wear the hi-vis, hard hat and boots provided",
  () => "I'll stay with my host and follow site rules",
];

export function SignInForm() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const sigRef = useRef<SignaturePadHandle>(null);

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [signInType, setSignInType] = useState<'worker' | 'visitor'>('worker');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [tradeRole, setTradeRole] = useState('');
  const [visitorHost, setVisitorHost] = useState('');
  const [mobile, setMobile] = useState('');
  const [vehicleReg, setVehicleReg] = useState('');
  // Worker confirmations are 4 ticks; visitor confirmations are 2. We size
  // this from the active set so toggling type doesn't carry stale state.
  const confirmationsForType = signInType === 'visitor' ? VISITOR_CONFIRMATIONS : WORKER_CONFIRMATIONS;
  const [confirms, setConfirms] = useState<boolean[]>([false, false, false, false]);
  const [isFirstAider, setIsFirstAider] = useState(false);

  /** Switch sign-in type. Resets the confirmations array to the right length,
   *  clears the type-specific field (trade vs host) so values can't leak
   *  across, and clears any stale company-check status from the worker path. */
  function setType(next: 'worker' | 'visitor') {
    setSignInType(next);
    setConfirms(next === 'visitor' ? [false, false] : [false, false, false, false]);
    if (next === 'visitor') {
      setTradeRole('');
      setCompanyCheck({ status: 'idle' });
      setOverrideReason('');
    } else {
      setVisitorHost('');
    }
  }
  const [recentOps, setRecentOps] = useState<SignInPrefillSource[]>([]);
  const [companyCheck, setCompanyCheck] = useState<{ status: 'idle' | 'checking' | 'unknown' | 'ok' | 'expired'; sub?: SubcontractorRow | null }>({ status: 'idle' });
  const [overrideReason, setOverrideReason] = useState('');

  // Live "on site now" panel — fetched on mount + after any sign-out.
  // Per the UX brief, this gives the site manager an immediate count at
  // the top of the screen plus a bulk sign-out button for "I'm closing
  // up the cabin, sign everyone out."
  const [activeOnSite, setActiveOnSite] = useState<SignIn[] | null>(null);
  const [bulkSigningOut, setBulkSigningOut] = useState(false);

  async function refreshActive() {
    if (!siteId) return;
    try {
      const list = await listActiveSignIns(siteId);
      setActiveOnSite(list);
    } catch {
      setActiveOnSite([]);
    }
  }

  useEffect(() => {
    refreshActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function onBulkSignOut() {
    if (!siteId || !activeOnSite || activeOnSite.length === 0) return;
    const n = activeOnSite.length;
    const ok = await confirm({
      title: `Sign everyone out?`,
      body: `This will sign out all ${n} ${n === 1 ? 'person' : 'people'} currently on site. Use this when you're closing the cabin at the end of the day.`,
      confirmLabel: 'Sign everyone out',
      danger: true,
    });
    if (!ok) return;
    setBulkSigningOut(true);
    try {
      await bulkSignOut(siteId, new Date().toISOString());
      await refreshActive();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkSigningOut(false);
    }
  }

  function fmtTime(iso: string): string {
    try {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  }

  useEffect(() => {
    if (!siteId || !org) return;
    Promise.all([
      getSite(siteId),
      listSignInPrefillSources(org.org_id, siteId).catch(() => []),
    ])
      .then(([s, ops]) => { setSite(s); setRecentOps(ops); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId, org]);

  async function checkCompany() {
    if (!org) return;
    const c = company.trim();
    if (!c) { setCompanyCheck({ status: 'idle' }); return; }
    if (c.toLowerCase() === org.org_name.trim().toLowerCase()) { setCompanyCheck({ status: 'ok', sub: null }); return; }
    setCompanyCheck({ status: 'checking' });
    try {
      const sub = await findSubcontractorByCompany(org.org_id, c);
      if (!sub) { setCompanyCheck({ status: 'unknown' }); return; }
      const now = Date.now();
      const expired = (sub.pl_insurance_expiry && new Date(sub.pl_insurance_expiry).getTime() < now) ||
        (sub.el_insurance_expiry && new Date(sub.el_insurance_expiry).getTime() < now);
      setCompanyCheck({ status: expired ? 'expired' : 'ok', sub });
    } catch {
      setCompanyCheck({ status: 'idle' });
    }
  }

  function prefillFrom(op: SignInPrefillSource) {
    setFullName(op.full_name);
    setCompany(op.company);
    setTradeRole(op.trade_role ?? '');
    setMobile(op.mobile ?? '');
    setVehicleReg(op.vehicle_reg ?? '');
    setIsFirstAider(op.is_first_aider);
    // Optimistic — they're either a known recent op or a known inducted
    // subbie. checkCompany() will still run on the company-field blur to
    // verify insurance status.
    setCompanyCheck({ status: 'ok' });
    // Scroll to the form so the user sees the pre-fill happen
    window.scrollTo({ top: 200, behavior: 'smooth' });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site) return;
    setError(null);

    // Required-field validation differs by type:
    //  - Worker: name + company + mobile required (mobile lets us call them
    //    in an emergency; subbie firms always have a contact number).
    //  - Visitor: name + host required (mobile + company optional — a delivery
    //    driver may not give either, but the host must know who they came to
    //    see for occupiers'-liability accountability).
    if (signInType === 'visitor') {
      if (!fullName.trim() || !visitorHost.trim()) {
        setError('Name and host (who you’re here to see) are required.');
        return;
      }
    } else {
      if (!fullName.trim() || !company.trim() || !mobile.trim()) {
        setError('Name, company and mobile are required.');
        return;
      }
    }
    // companyCheck === 'unknown' used to hard-block the form. External UX
    // review flagged this as the top reason gaffers abandoned the feature:
    // a visiting subbie turns up unannounced, the form refuses, the worker
    // ends up scrawled on a tape gun. Now we accept the sign-in and stamp
    // the row with requires_induction so the org admin can follow up.
    // Visitors skip the induction check entirely — a delivery driver isn't
    // a subbie we'll ever induct.
    if (signInType === 'worker' && companyCheck.status === 'expired' && !overrideReason.trim()) {
      setError('Insurance has expired. Add a site-manager override reason to continue.');
      return;
    }
    if (!confirms.every(Boolean)) {
      const n = confirmationsForType.length;
      setError(`Please tick all ${n === 4 ? 'four' : 'two'} confirmations to continue.`);
      return;
    }
    if (sigRef.current?.isEmpty()) {
      setError('Please sign in the box above.');
      return;
    }

    setSubmitting(true);
    try {
      // Capture geolocation in parallel with the rest of submit (don't block on it).
      const geoPromise = captureGeolocation(5000);
      const blob = await sigRef.current!.toBlob();
      const geo = await geoPromise;

      // Geofence check: if we have a fix and the site has a centre set,
      // warn if the user appears to be off-site.
      if (
        geo &&
        site.centre_lat != null &&
        site.centre_lng != null
      ) {
        const dist = distanceMeters(geo.lat, geo.lng, site.centre_lat, site.centre_lng);
        const radius = site.geofence_radius_m ?? 200;
        if (dist > radius) {
          const ok = await confirm({
            title: 'You look off-site',
            body: `You appear to be about ${Math.round(dist)}m from the site centre (geofence is ${radius}m). Continue signing in?`,
            confirmLabel: 'Sign in anyway',
          });
          if (!ok) {
            setSubmitting(false);
            return;
          }
        }
      }

      // First successful sign-in to a fresh site auto-bootstraps the centre,
      // so subsequent geofence checks have something to compare against.
      if (geo && (site.centre_lat == null || site.centre_lng == null)) {
        try {
          await updateSite(site.id, {
            centre_lat: geo.lat,
            centre_lng: geo.lng,
          });
        } catch {
          /* non-fatal */
        }
      }

      // 1. Insert the sign_in row.
      const row = await createSignIn({
        orgId: org.org_id,
        siteId: site.id,
        full_name: fullName.trim(),
        company: company.trim() || (signInType === 'visitor' ? '—' : ''),
        trade_role: signInType === 'visitor' ? null : (tradeRole.trim() || null),
        mobile: mobile.trim() || null,
        vehicle_reg: vehicleReg.trim() || null,
        override_reason: signInType === 'worker' && companyCheck.status === 'expired' ? overrideReason.trim() : null,
        in_lat: geo?.lat ?? null,
        in_lng: geo?.lng ?? null,
        in_address: geo?.address ?? null,
        in_accuracy_m: geo?.accuracy_m ?? null,
        confirmations_accepted: confirms,
        is_first_aider: signInType === 'visitor' ? false : isFirstAider,
        // Only worker sign-ins set the induction-follow-up flag; visitors
        // never need induction so we don't pollute the dashboard with them.
        requires_induction: signInType === 'worker' && companyCheck.status === 'unknown',
        sign_in_type: signInType,
        visitor_host: signInType === 'visitor' ? visitorHost.trim() : null,
      });

      // 2. Upload signature to Storage.
      const path = await uploadSignature({
        orgId: org.org_id,
        signInId: row.id,
        side: 'in',
        blob,
      });

      // 3. Update the row with the signature path.
      await setSignInSignaturePath(row.id, 'in', path);

      nav(`/sites/${site.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppShell title="Sign in" back={`/sites/${siteId}`}>
        <LoadingIndicator />
      </AppShell>
    );
  }

  if (!site || !org) {
    return (
      <AppShell title="Sign in" back={`/sites/${siteId}`}>
        <div className="banner banner-error">{error ?? 'Site not found.'}</div>
      </AppShell>
    );
  }

  return (
    <AppShell title={`Sign in · ${site.name}`} back={`/sites/${site.id}`}>
      {/* "On site now" panel — live count + name list + bulk sign-out.
          Sits at the very top so the site manager sees the number
          immediately. Hidden until the active-sign-in fetch resolves to
          avoid a flash of "0 on site" while loading. */}
      {activeOnSite !== null && (
        <div className="card" style={{ marginBottom: 14, background: 'var(--card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                On site now
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
                {activeOnSite.length}
              </p>
            </div>
            {activeOnSite.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onBulkSignOut}
                disabled={bulkSigningOut}
                style={{ flexShrink: 0 }}
              >
                {bulkSigningOut ? 'Signing out…' : '↩ Sign everyone out'}
              </button>
            )}
          </div>

          {activeOnSite.length > 0 && (
            <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, borderTop: '1px solid var(--border)' }}>
              {activeOnSite.map((s) => (
                <li
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <span
                    aria-label="on site"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: 'var(--success, #16A34A)',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.full_name}
                    </p>
                    {s.company && (
                      <p className="signin-card-meta" style={{ margin: '1px 0 0' }}>
                        {s.company}
                      </p>
                    )}
                  </div>
                  <span className="muted" style={{ fontSize: 12, flexShrink: 0 }}>
                    Signed in {fmtTime(s.time_in)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Type picker — two big tappable cards. Defaults to Worker because
          that's by far the most common case. Visitor is for delivery drivers,
          surveyors, inspectors, family etc. — short form, no induction
          check, host name instead of trade. */}
      <div
        role="radiogroup"
        aria-label="Type of sign-in"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}
      >
        <button
          type="button"
          role="radio"
          aria-checked={signInType === 'worker'}
          onClick={() => setType('worker')}
          className="signin-type-card"
          data-active={signInType === 'worker'}
        >
          <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">🦺</span>
          <strong>I'm working on site</strong>
          <span className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Subbie, labourer, foreman
          </span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={signInType === 'visitor'}
          onClick={() => setType('visitor')}
          className="signin-type-card"
          data-active={signInType === 'visitor'}
        >
          <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">🚚</span>
          <strong>I'm a visitor</strong>
          <span className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Delivery, surveyor, inspector
          </span>
        </button>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}>
          {signInType === 'visitor'
            ? 'Quick sign-in for visitors. You\'ll be logged on site until you sign out, and your host knows to look after you.'
            : 'Sign yourself in by completing this form. By signing you confirm you\'ve been inducted, hold the right cards and insurance, and will follow site rules.'}
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {signInType === 'worker' && recentOps.length > 0 && !fullName.trim() && (
        <div className="card" style={{ marginBottom: 16, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Tap your name to pre-fill</p>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Recent sign-ins and anyone already inducted with us. New here? Skip and fill the form below.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {recentOps.map((op, i) => (
              <button
                key={i}
                type="button"
                className="recent-op-chip"
                onClick={() => prefillFrom(op)}
                title={op.source === 'inducted'
                  ? 'Inducted with us — tap to pre-fill from your subbie record'
                  : 'Recently signed in on this site — tap to pre-fill'}
              >
                <strong>{op.full_name}</strong>
                <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                  {op.company}
                </span>
                {op.source === 'inducted' && (
                  <span
                    className="status-pill status-muted"
                    style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px' }}
                  >
                    inducted
                  </span>
                )}
                {op.is_first_aider && (
                  <span
                    className="status-pill status-ok"
                    style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px' }}
                    aria-label="qualified first aider"
                    title="Qualified first aider"
                  >
                    🩹 FA
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="f-name">Full name *</label>
          <input
            id="f-name"
            type="text"
            autoComplete="name"
            enterKeyHint="next"
            required
            placeholder="e.g. Mike Doyle" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="f-company">
            Company {signInType === 'visitor' ? '' : '*'}
            {signInType === 'visitor' && (
              <span className="muted" style={{ fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                (optional)
              </span>
            )}
          </label>
          <input
            id="f-company"
            type="text"
            required={signInType !== 'visitor'}
            enterKeyHint="next"
            placeholder={signInType === 'visitor' ? 'e.g. AB Concrete Ltd (if any)' : 'e.g. ACME Bricklaying Ltd'}
            value={company}
            onChange={(e) => { setCompany(e.target.value); if (companyCheck.status !== 'idle') setCompanyCheck({ status: 'idle' }); }}
            onBlur={signInType === 'worker' ? checkCompany : undefined}
          />
          {/* Induction check + warnings only apply to workers — a visitor isn't
              someone we'll ever induct. */}
          {signInType === 'worker' && companyCheck.status === 'unknown' && (
            <div
              className="banner banner-warn"
              style={{
                marginTop: 6,
                fontSize: 13,
                background: '#FFF8E5',
                borderColor: 'var(--warn)',
                color: 'var(--ink)',
              }}
            >
              <strong>⚠ Not yet inducted</strong> — we don't have <strong>{company}</strong> on file.
              You can still sign in now; we'll flag this for follow-up so an admin can run the induction
              before their next visit.{' '}
              <Link
                to={`/sites/${siteId ?? ''}/forms/subcontractor_induction`}
                style={{ fontWeight: 600, textDecoration: 'underline' }}
              >
                Or induct now →
              </Link>
            </div>
          )}
          {signInType === 'worker' && companyCheck.status === 'expired' && (
            <div className="banner banner-warn" style={{ marginTop: 6, fontSize: 13, background: '#FFF8E5', borderColor: 'var(--warn)', color: 'var(--ink)' }}>
              <strong>⚠ Insurance has expired</strong> on this company's record.
              {companyCheck.sub?.pl_insurance_expiry && new Date(companyCheck.sub.pl_insurance_expiry) < new Date() && (
                <span> Public Liability expired {new Date(companyCheck.sub.pl_insurance_expiry).toLocaleDateString('en-GB')}.</span>
              )}
              {companyCheck.sub?.el_insurance_expiry && new Date(companyCheck.sub.el_insurance_expiry) < new Date() && (
                <span> Employers Liability expired {new Date(companyCheck.sub.el_insurance_expiry).toLocaleDateString('en-GB')}.</span>
              )}
            </div>
          )}
          {signInType === 'worker' && companyCheck.status === 'ok' && companyCheck.sub && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 12, color: '#16A34A' }}>✓ Inducted, insurance current.</p>
          )}
        </div>
        {signInType === 'worker' && companyCheck.status === 'expired' && (
          <div className="field needs-input">
            <label htmlFor="f-override">Site-manager override reason *</label>
            <input
              id="f-override"
              type="text"
              placeholder="e.g. Renewal cert in hand, awaiting paperwork"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              Logged on the sign-in record. Use only if you've personally checked the renewal is in progress.
            </p>
          </div>
        )}

        {signInType === 'visitor' ? (
          <div className="field">
            <label htmlFor="f-host">Who are you here to see? *</label>
            <input
              id="f-host"
              type="text"
              required
              enterKeyHint="next"
              placeholder="e.g. Will Yates (site manager)"
              value={visitorHost}
              onChange={(e) => setVisitorHost(e.target.value)}
            />
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              The person on site responsible for you. They'll know to look after you.
            </p>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="f-trade">Trade / role</label>
            <input
              id="f-trade"
              type="text"
              enterKeyHint="next"
              placeholder="e.g. Bricklayer"
              value={tradeRole}
              onChange={(e) => setTradeRole(e.target.value)}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="f-mobile">
            Mobile {signInType === 'visitor' ? '' : '*'}
            {signInType === 'visitor' && (
              <span className="muted" style={{ fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                (optional)
              </span>
            )}
          </label>
          <input
            id="f-mobile"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            enterKeyHint="next"
            required={signInType !== 'visitor'}
            placeholder="e.g. 07700 900100" value={mobile}
            onChange={(e) => setMobile(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="f-vehicle">Vehicle reg (if driving)</label>
          <input
            id="f-vehicle"
            type="text"
            autoCapitalize="characters"
            placeholder="e.g. AB12 CDE — leave blank if none" value={vehicleReg}
            onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
          />
        </div>

        {signInType === 'worker' && (
          <div className="field" style={{ marginTop: 4 }}>
            <label className="check-row" style={{ alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={isFirstAider}
                onChange={(e) => setIsFirstAider(e.target.checked)}
              />
              <span>
                <strong>I'm a qualified first aider</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                  Tick if you hold a current First Aid at Work, Emergency First Aid at Work, or equivalent certificate. Helps the site know who to call if something happens.
                </span>
              </span>
            </label>
          </div>
        )}

        <h3 className="section-title" style={{ marginTop: 22 }}>Confirmations</h3>
        <div className="check-list">
          {confirmationsForType.map((make, i) => (
            <label key={`${signInType}-${i}`} className="check-row">
              <input
                type="checkbox"
                checked={!!confirms[i]}
                onChange={(e) => {
                  const next = confirms.slice();
                  next[i] = e.target.checked;
                  setConfirms(next);
                }}
              />
              <span>{make(org.org_name)}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Signature *</h3>
        <SignaturePad ref={sigRef} />

        <div className="btn-stack">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav(`/sites/${site.id}`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
